import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logCorreoMasivo } from '../../services/seguimientoService';

export const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const trimmed = email.trim();
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return re.test(trimmed);
};

export const BulkPromotionModal = ({ clientes, giro, onClose }) => {
    const { uid, displayName } = useSelector(state => state.auth);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    
    // Adjunto
    const [attachedFile, setAttachedFile] = useState(null);
    const [fileBase64, setFileBase64] = useState('');
    const [fileName, setFileName] = useState('');

    // Filtrar clientes por validez de su correo
    const validClientes = clientes.filter(c => isValidEmail(c.email));
    const invalidEmailClientes = clientes.filter(c => !!c.email && !isValidEmail(c.email));
    const noEmailClientes = clientes.filter(c => !c.email || !c.email.trim());

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setAttachedFile(null);
            setFileBase64('');
            setFileName('');
            return;
        }

        // Limitar tamaño a 5MB para Cloud Functions
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'Archivo muy grande',
                text: 'El tamaño máximo permitido para adjuntos es de 5MB.',
            });
            e.target.value = ''; // Limpiar el input
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            // El resultado viene con el prefijo "data:application/pdf;base64,"
            // Brevo necesita únicamente la parte codificada en base64 pura.
            const base64Content = reader.result.split(',')[1];
            setAttachedFile(file);
            setFileBase64(base64Content);
            setFileName(file.name);
        };
        reader.onerror = (error) => {
            console.error('Error al leer el archivo:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de lectura',
                text: 'No se pudo procesar el archivo seleccionado.',
            });
        };
        reader.readAsDataURL(file);
    };

    const handleSend = async () => {
        if (!subject.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Asunto requerido',
                text: 'Por favor ingresa un asunto para el correo.',
                toast: true,
                position: 'top-end',
                timer: 3000,
                showConfirmButton: false,
            });
            return;
        }

        if (!message.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Mensaje vacío',
                text: 'Por favor escribe el cuerpo de la promoción antes de enviar.',
                toast: true,
                position: 'top-end',
                timer: 3000,
                showConfirmButton: false,
            });
            return;
        }

        if (validClientes.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Sin destinatarios válidos',
                text: 'Ninguno de los clientes de este giro tiene un correo electrónico válido registrado.',
            });
            return;
        }

        setSending(true);
        try {
            const functions = getFunctions();
            const sendBulkPromotions = httpsCallable(functions, 'sendBulkPromotions');

            // Construimos la petición únicamente con los correos válidos y limpios
            const payload = {
                emails: validClientes.map(c => c.email.trim()),
                subject,
                message
            };

            if (fileBase64 && fileName) {
                payload.attachmentBase64 = fileBase64;
                payload.attachmentName = fileName;
            }

            const response = await sendBulkPromotions(payload);
            const data = response?.data || {};
            const totalSent = data.totalSent || 0;
            const totalFailed = data.totalFailed || 0;
            const failedList = data.failed || [];

            // Registrar evento en correoLogs solo con la cantidad de correos efectivamente enviados
            if (totalSent > 0) {
                try {
                    await logCorreoMasivo({
                        usuarioId: uid || 'desconocido',
                        usuarioNombre: displayName || 'Usuario',
                        giro,
                        cantidadDestinatarios: totalSent,
                    });
                } catch (logErr) {
                    console.warn('No se pudo registrar el log de correo:', logErr);
                }
            }

            if (totalSent > 0 && totalFailed === 0) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Promoción enviada!',
                    text: `Se enviaron correos con éxito a los ${totalSent} clientes del giro "${giro}".`,
                });
                onClose();
            } else if (totalSent > 0 && totalFailed > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Envío parcial completado',
                    html: `
                        <p style="margin-bottom: 10px;">Se enviaron <b>${totalSent}</b> correos con éxito.</p>
                        <p style="color: #b45309; font-weight: 600; margin-bottom: 6px;"><b>${totalFailed}</b> correos no pudieron enviarse:</p>
                        <div style="max-height: 140px; overflow-y: auto; text-align: left; background: #fef3c7; padding: 10px; border-radius: 6px; font-size: 12px; color: #92400e; border: 1px solid #fde68a;">
                            ${failedList.map(f => `<div style="margin-bottom: 4px;">• <b>${f.email}</b>: ${f.reason}</div>`).join('')}
                        </div>
                    `,
                });
                onClose();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'No se pudo enviar la promoción',
                    html: `
                        <p style="margin-bottom: 10px;">Ningún correo pudo ser entregado:</p>
                        <div style="max-height: 140px; overflow-y: auto; text-align: left; background: #fee2e2; padding: 10px; border-radius: 6px; font-size: 12px; color: #991b1b; border: 1px solid #fecaca;">
                            ${failedList.length > 0 
                                ? failedList.map(f => `<div style="margin-bottom: 4px;">• <b>${f.email}</b>: ${f.reason}</div>`).join('')
                                : (data.message || 'Error desconocido al enviar.')
                            }
                        </div>
                    `,
                });
            }
        } catch (err) {
            console.error('sendBulkPromotions error:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error al enviar',
                text: err?.message || 'Hubo un problema al enviar la promoción. Intenta de nuevo.',
            });
        } finally {
            setSending(false);
        }
    };

    return (
        /* ── Backdrop ───────────────────────────────────────── */
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'fadeIn 0.2s ease',
            }}
        >
            <style>{`
                @keyframes fadeIn  { from { opacity:0; transform:scale(.95); } to { opacity:1; transform:scale(1); } }
                .promo-input { width:100%; border:1.5px solid #d1d5db; border-radius:8px; padding:10px 12px; font-size:14px; outline:none; transition:border-color .18s; box-sizing:border-box; }
                .promo-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }
                .promo-textarea { width:100%; min-height:140px; resize:vertical; border:1.5px solid #d1d5db; border-radius:8px; padding:12px; font-size:14px; font-family:inherit; line-height:1.6; outline:none; transition:border-color .18s; box-sizing:border-box; }
                .promo-textarea:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }
                .promo-btn-send { background:linear-gradient(135deg,#3b82f6,#1d4ed8); color:#fff; border:none; border-radius:8px; padding:10px 24px; font-size:15px; font-weight:600; cursor:pointer; transition:transform .15s,box-shadow .15s; }
                .promo-btn-send:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(29,78,216,.35); }
                .promo-btn-send:disabled { opacity:.55; cursor:not-allowed; }
                .promo-btn-cancel { background:#f3f4f6; color:#374151; border:none; border-radius:8px; padding:10px 20px; font-size:15px; font-weight:600; cursor:pointer; transition:background .15s; }
                .promo-btn-cancel:hover { background:#e5e7eb; }
                
                /* File Input custom styling */
                .promo-file-upload { border:1.5px dashed #d1d5db; border-radius:8px; padding:12px; text-align:center; cursor:pointer; transition:border-color .18s, background .18s; position:relative; background:#f9fafb; margin-top:10px; }
                .promo-file-upload:hover { border-color:#3b82f6; background:#f5f3ff; }
                .promo-file-input { position:absolute; top:0; left:0; width:100%; height:100%; opacity:0; cursor:pointer; }
                
                /* Recipient Box styling */
                .promo-recipient-box { max-height:80px; overflow-y:auto; border:1px solid #e5e7eb; border-radius:6px; padding:8px; background:#f9fafb; font-size:12px; color:#4b5563; line-height:1.5; }
            `}</style>

            {/* ── Card ───────────────────────────────────────── */}
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 25px 60px rgba(0,0,0,.2)',
                width: '90%',
                maxWidth: '560px',
                padding: '30px',
                animation: 'fadeIn 0.22s ease',
            }}>
                {/* Header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}>
                    <div>
                        <h2 style={{ margin:0, fontSize:'20px', fontWeight:'700', color:'#111827' }}>
                            ✉️ Enviar Promoción por Lote
                        </h2>
                        <p style={{ margin:'4px 0 0', fontSize:'14px', color:'#6b7280' }}>
                            Giro: <strong>{giro}</strong>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background:'none', border:'none', fontSize:'22px', cursor:'pointer', color:'#9ca3af', lineHeight:1 }}
                        title="Cerrar"
                    >
                        ×
                    </button>
                </div>

                {/* Destinatarios */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px' }}>
                        <span>Destinatarios válidos ({validClientes.length})</span>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '11px', flexWrap: 'wrap' }}>
                            {invalidEmailClientes.length > 0 && (
                                <span 
                                    style={{ color: '#d97706', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', cursor: 'help' }} 
                                    title={invalidEmailClientes.map(c => `${c.nombre}: "${c.email}"`).join('\n')}
                                >
                                    ⚠️ {invalidEmailClientes.length} inválido(s)
                                </span>
                            )}
                            {noEmailClientes.length > 0 && (
                                <span 
                                    style={{ color: '#dc2626', background: '#fee2e2', padding: '2px 6px', borderRadius: '4px', cursor: 'help' }} 
                                    title={noEmailClientes.map(c => c.nombre).join('\n')}
                                >
                                    ⚠️ {noEmailClientes.length} sin correo
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="promo-recipient-box">
                        {validClientes.length > 0 ? (
                            validClientes.map(c => `${c.nombre} (${c.email.trim()})`).join(', ')
                        ) : (
                            <span style={{ color: '#ef4444' }}>No hay clientes con correo electrónico válido.</span>
                        )}
                    </div>
                </div>

                {/* Asunto */}
                <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Asunto del correo
                    </label>
                    <input 
                        type="text" 
                        className="promo-input" 
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Ej. ¡Gran descuento de temporada!"
                        disabled={sending}
                    />
                </div>

                {/* Mensaje */}
                <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Mensaje / Contenido de la Promoción
                    </label>
                    <textarea
                        className="promo-textarea"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Escribe aquí el contenido promocional que recibirá cada cliente de forma individual..."
                        disabled={sending}
                    />
                </div>

                {/* Adjunto */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4b5563', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Archivo Adjunto (PDF, Imagen, etc. Máx 5MB)
                    </label>
                    <div className="promo-file-upload">
                        <input 
                            type="file" 
                            className="promo-file-input" 
                            onChange={handleFileChange}
                            disabled={sending}
                        />
                        <span style={{ fontSize: '13px', color: '#4b5563' }}>
                            {attachedFile ? `📎 ${attachedFile.name} (${(attachedFile.size / 1024).toFixed(1)} KB)` : '📁 Selecciona o arrastra el archivo de promoción'}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                    <button className="promo-btn-cancel" onClick={onClose} disabled={sending}>
                        Cancelar
                    </button>
                    <button className="promo-btn-send" onClick={handleSend} disabled={sending || validClientes.length === 0}>
                        {sending ? '⏳ Enviando promoción...' : '✉️ Enviar promoción'}
                    </button>
                </div>
            </div>
        </div>
    );
};
